import React from 'react';
import classNames from 'classnames';
import i18n from '@/content';
import { PolicySetting } from '@/types/types';
import { PolicySettingsSubgroup } from './settingsGroups';
import { SETTINGS_NOT_STYLED_AS_CHILD } from './constants';
import styles from './PolicySection.module.scss';

interface SettingInfo {
    setting: PolicySetting;
    content: React.ReactNode;
    isChild: boolean;
    hasSubDivider: boolean;
    isVisible: boolean;
}

interface RenderedSubgroup {
    subgroup: PolicySettingsSubgroup;
    content: React.ReactNode;
    hasContent: boolean;
}

/**
 * Render a list of settings with proper border styling
 */
const renderSettingsWithBorders = (settingsInfo: SettingInfo[]): React.ReactNode[] => settingsInfo
    .map((info, index) => {
        if (!info.isVisible) return null;

        // Find the next visible setting to check if it's a child or has a sub-divider
        let nextIsChild = false;
        let nextHasSubDivider = false;
        for (let i = index + 1; i < settingsInfo.length; i++) {
            if (settingsInfo[i].isVisible) {
                nextIsChild = settingsInfo[i].isChild;
                nextHasSubDivider = settingsInfo[i].hasSubDivider;
                break;
            }
        }

        return (
            <div
                key={info.setting.id}
                className={classNames(
                    styles.settingWrapper,
                    {
                        [styles.childSetting]: info.isChild,
                        [styles.noBorder]: nextIsChild,
                        [styles.hasSubDividerBelow]: nextHasSubDivider,
                        [styles.hasSubDivider]: info.hasSubDivider,
                    },
                )}
            >
                {info.hasSubDivider && (
                    <div className={classNames(styles.fullDivider, styles['fullDivider--inlineSpacing'])} />
                )}
                {info.content}
            </div>
        );
    })
    .filter(Boolean);

/**
 * Render a single policy settings subgroup
 */
const renderSubgroup = (
    subgroup: PolicySettingsSubgroup,
    settingsInfo: SettingInfo[],
): RenderedSubgroup => {
    const renderedSettings = renderSettingsWithBorders(settingsInfo);
    const hasContent = renderedSettings.length > 0;

    const content = hasContent ? (
        <div className={styles.subgroup}>
            <div className={styles.subgroupTitle}>
                <div className={styles.subgroupLabel}>
                    {i18n.exists(subgroup.titleKey)
                        ? i18n.t(subgroup.titleKey)
                        : subgroup.titleKey}
                </div>
                {subgroup.descriptionKey && i18n.exists(subgroup.descriptionKey) && (
                    <div className={styles.topLabel}>
                        {i18n.t(subgroup.descriptionKey)}
                    </div>
                )}
                {subgroup.linkConfig && (
                    <div className={styles.subgroupLink}>
                        <button type="button">
                            {i18n.t(subgroup.linkConfig.textKey)}
                        </button>
                    </div>
                )}
            </div>
            <div className={styles.controlsGroup}>
                {renderedSettings}
            </div>
        </div>
    ) : null;

    return {
        subgroup,
        content,
        hasContent,
    };
};

interface RenderGroupedSettingsParams {
    groupedSettings: Array<{
        settingIds: string[];
        settings: PolicySetting[];
        titleKey: string;
        descriptionKey?: string;
        linkConfig?: {
            textKey: string;
            action: string;
        };
    }>;
    renderSetting: (setting: PolicySetting) => React.ReactNode;
    MAP_UI_CHILD_TO_PARENT: Record<string, string>;
}

/**
 * Render all grouped settings with proper organization
 * This replaces the complex double-pass logic in PolicySection
 */
const renderGroupedSettings = ({
    groupedSettings,
    renderSetting,
    MAP_UI_CHILD_TO_PARENT,
}: RenderGroupedSettingsParams): React.ReactNode => {
    const renderedSubgroups: RenderedSubgroup[] = [];

    groupedSettings.forEach((subgroup) => {
        const settingsInfo: SettingInfo[] = subgroup.settings.map((setting) => {
            const content = renderSetting(setting);
            const dependencyId: string = setting.dependency || MAP_UI_CHILD_TO_PARENT[setting.id];
            const hasParentDependency = !!dependencyId;
            const shouldNotStyleAsChild = SETTINGS_NOT_STYLED_AS_CHILD.has(setting.id);
            const isChild = hasParentDependency && !shouldNotStyleAsChild;
            const hasSubDivider = shouldNotStyleAsChild;

            return {
                setting,
                content,
                isChild,
                hasSubDivider,
                isVisible: content !== null,
            };
        });

        const rendered = renderSubgroup(subgroup, settingsInfo);

        if (rendered.hasContent) {
            renderedSubgroups.push(rendered);
        }
    });

    return renderedSubgroups.map((item) => (
        <React.Fragment key={item.subgroup.titleKey}>
            <div className={styles.fullDivider} />
            {item.content}
        </React.Fragment>
    ));
};

export default renderGroupedSettings;
